/**
 * Visual Dictionary Component
 * 
 * A comprehensive showcase of all UI components available in the application.
 * This page serves as a reference for developers and designers.
 */
import { useState, useEffect } from 'react'
import Button from '../ui/button'
import Input from '../ui/input'
import Select from '../ui/select'
import Checkbox from '../ui/checkbox'
import Textarea from '../ui/textarea'
import Alert from '../ui/alert'
import Badge from '../ui/badge'
import Tooltip from '../ui/tooltip'
import Modal from '../ui/modal'
import Popover from '../ui/popover'
import Tabs from '../ui/tabs'
import Pagination from '../ui/pagination'
import Skeleton from '../ui/skeleton'
import Switch from '../ui/switch'
import Card from '../ui/card'
import ThemeToggle from '../ui/theme-toggle'

function VisualDictionary() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [switchValue, setSwitchValue] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [currentPage, setCurrentPage] = useState(1)

  // Add CSS to improve tooltip and popover positioning
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style')
    
    // Add CSS rules to fix tooltip and popover positioning
    style.textContent = `
      /* Ensure tooltips and popovers have smooth transitions */
      [data-tooltip], [data-popover] {
        opacity: 0;
        transition: opacity 0.2s, transform 0.2s;
        transform: translateY(10px);
      }
      
      [data-tooltip][data-state="visible"],
      [data-popover][data-state="visible"] {
        opacity: 1;
        transform: translateY(0);
      }
    `
    
    // Append the style element to the document head
    document.head.appendChild(style)
    
    // Clean up when component unmounts
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // Demo options for select component
  const selectOptions = [
    { value: 'chess', label: 'Chess' },
    { value: 'monopoly', label: 'Monopoly' },
    { value: 'catan', label: 'Settlers of Catan' },
    { value: 'scrabble', label: 'Scrabble' },
  ]

  return (
    <div className="space-y-12">
      <div className="border-b pb-4">
        <h1 className="text-3xl font-bold mb-2">UI Component Visual Dictionary</h1>
        <p className="text-muted-foreground">
          A comprehensive showcase of all UI components available in the Boardgames Today application.
          Use this page as a reference when building new features.
        </p>
      </div>

      <Tabs
        tabs={[
          { id: 'basic', label: 'Basic Components', content: null },
          { id: 'feedback', label: 'Feedback Components', content: null },
          { id: 'navigation', label: 'Navigation Components', content: null },
          { id: 'utility', label: 'Utility Components', content: null },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'basic' && (
        <section className="space-y-8">
          <h2 className="text-2xl font-semibold">Basic Components</h2>

          {/* Button Component */}
          <ComponentCard title="Button" description="A versatile button component with multiple variants and sizes">
            <div className="flex flex-wrap gap-4">
              <Button>Default</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
              <Button disabled>Disabled</Button>
              <Button size="sm">Small</Button>
              <Button size="lg">Large</Button>
            </div>
          </ComponentCard>

          {/* Input Component */}
          <ComponentCard title="Input" description="Form input field with validation states and error handling">
            <div className="space-y-4 max-w-md">
              <Input placeholder="Default input" />
              <Input placeholder="Disabled input" disabled />
              <div>
                <Input placeholder="Input with error" className="border-red-500" />
                <p className="text-red-500 text-sm mt-1">This field is required</p>
              </div>
            </div>
          </ComponentCard>

          {/* Select Component */}
          <ComponentCard title="Select" description="Customizable dropdown select component with search">
            <div className="max-w-md">
              <Select 
                options={selectOptions}
                placeholder="Select a board game"
                onChange={(value) => console.log(value)}
              />
            </div>
          </ComponentCard>

          {/* Checkbox Component */}
          <ComponentCard title="Checkbox" description="Toggle input with label and error support">
            <div className="space-y-2">
              <Checkbox 
                label="I agree to the terms and conditions" 
                checked={isChecked} 
                onChange={() => setIsChecked(!isChecked)} 
              />
              <Checkbox 
                label="Disabled checkbox" 
                disabled 
              />
            </div>
          </ComponentCard>

          {/* Textarea Component */}
          <ComponentCard title="Textarea" description="Multi-line text input with auto-resizing">
            <div className="max-w-md">
              <Textarea 
                placeholder="Enter your message here..." 
                rows={4}
              />
            </div>
          </ComponentCard>

          {/* Card Component */}
          <ComponentCard title="Card" description="Container for related content and actions">
            <Card className="max-w-md p-6">
              <h3 className="text-xl font-semibold mb-2">Card Title</h3>
              <p className="text-muted-foreground mb-4">This is a card component that can be used to group related content.</p>
              <Button>Card Action</Button>
            </Card>
          </ComponentCard>
        </section>
      )}

      {activeTab === 'feedback' && (
        <section className="space-y-8">
          <h2 className="text-2xl font-semibold">Feedback Components</h2>

          {/* Alert Component */}
          <ComponentCard title="Alert" description="Displays messages with different severity levels">
            <div className="space-y-4">
              <Alert variant="info" title="Information">This is an informational alert.</Alert>
              <Alert variant="success" title="Success">Your changes have been saved successfully.</Alert>
              <Alert variant="warning" title="Warning">This action cannot be undone.</Alert>
              <Alert variant="default" title="Error">An error occurred while processing your request.</Alert>
            </div>
          </ComponentCard>

          {/* Badge Component */}
          <ComponentCard title="Badge" description="Small status indicators for labels or counts">
            <div className="flex flex-wrap gap-4">
              <Badge>Default</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
              <Badge variant="info">Error</Badge>
            </div>
          </ComponentCard>

          {/* Tooltip Component */}
          <ComponentCard title="Tooltip" description="Displays additional information on hover">
            <div className="flex justify-center py-8 relative min-h-[60px]">
              <Tooltip 
                content="This is a helpful tooltip"
                position="top"
              >
                <Button>Hover Me</Button>
              </Tooltip>
            </div>
          </ComponentCard>

          {/* Modal Component */}
          <ComponentCard title="Modal" description="Dialog box for focused interactions">
            <div>
              <Button onClick={() => setIsModalOpen(true)}>Open Modal</Button>
              <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Modal Title"
              >
                <div className="p-4">
                  <p className="mb-4">This is the content of the modal dialog.</p>
                  <Button onClick={() => setIsModalOpen(false)}>Close</Button>
                </div>
              </Modal>
            </div>
          </ComponentCard>

          {/* Popover Component */}
          <ComponentCard title="Popover" description="Floating panel triggered by click or hover">
            <div className="flex justify-center py-8 relative min-h-[60px]">
              <Popover
                trigger="click"
                position="bottom"
                align="center"
                content={
                  <div className="p-4 w-64">
                    <h3 className="font-medium mb-2">Popover Title</h3>
                    <p className="text-sm text-muted-foreground">This is a popover with some content inside.</p>
                  </div>
                }
              >
                <Button>Click Me</Button>
              </Popover>
            </div>
          </ComponentCard>
        </section>
      )}

      {activeTab === 'navigation' && (
        <section className="space-y-8">
          <h2 className="text-2xl font-semibold">Navigation Components</h2>

          {/* Tabs Component */}
          <ComponentCard title="Tabs" description="Organizes content into separate views">
            <div className="border rounded-lg p-4">
              <Tabs
                tabs={[
                  { id: 'tab1', label: 'Tab 1', content: <p>Content for Tab 1</p> },
                  { id: 'tab2', label: 'Tab 2', content: <p>Content for Tab 2</p> },
                  { id: 'tab3', label: 'Tab 3', content: <p>Content for Tab 3</p> },
                ]}
                activeTab="tab1"
                onTabChange={(tabId: string) => console.log(`Tab changed to ${tabId}`)}
              />
            </div>
          </ComponentCard>

          {/* Pagination Component */}
          <ComponentCard title="Pagination" description="Navigation for multi-page content">
            <Pagination
              currentPage={currentPage}
              totalPages={10}
              onPageChange={setCurrentPage}
            />
          </ComponentCard>
        </section>
      )}

      {activeTab === 'utility' && (
        <section className="space-y-8">
          <h2 className="text-2xl font-semibold">Utility Components</h2>

          {/* Skeleton Component */}
          <ComponentCard title="Skeleton" description="Loading state placeholders">
            <div className="space-y-4 max-w-md">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <div className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-4 w-[160px]" />
                </div>
              </div>
            </div>
          </ComponentCard>

          {/* Switch Component */}
          <ComponentCard title="Switch" description="Toggle switch for boolean inputs">
            <div className="flex items-center space-x-2">
              <Switch
                checked={switchValue}
                onChange={() => setSwitchValue(!switchValue)}
                label="Enable notifications"
              />
            </div>
          </ComponentCard>

          {/* Theme Toggle Component */}
          <ComponentCard title="Theme Toggle" description="Toggle between light and dark themes">
            <div className="flex justify-center">
              <ThemeToggle />
            </div>
          </ComponentCard>
        </section>
      )}
    </div>
  )
}

// Helper component to display each UI component with consistent styling
function ComponentCard({ 
  title, 
  description, 
  children 
}: { 
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="border rounded-xl overflow-hidden">
      <div className="bg-muted p-4 border-b">
        <h3 className="text-xl font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="p-6 bg-card">{children}</div>
      <div className="bg-muted p-4 border-t text-sm">
        <code className="text-xs">import {`{${title}}`} from '@/components/ui/{title.toLowerCase()}'</code>
      </div>
    </div>
  )
}

export default VisualDictionary
